import { Product, ProductSpecs } from "@/lib/data";
import SpecIcon from "@/components/ui/SpecIcon";
import SpecDateline from "@/components/products/SpecDateline";

interface ProductOverviewProps {
  product: Product;
  /** CMS `specs_revisadas_em`. ProductDetail passes it here ONLY when the
   *  variants table (ModelSpecTable) won't render — 47 of the 84 products
   *  have no spec table, and on those this is the only spec table on the
   *  page. Exactly one of the two components ever receives it. */
  revisedAt?: string;
}

/**
 * Legacy fallback table — only used when the CMS hasn't populated
 * `specEntries` yet (older records, or products that haven't been
 * re-saved since the Phase 1 schema migration). New records render
 * straight from `product.specEntries` with author-controlled label,
 * icon, and value formatting (text/number/range with unit).
 *
 * Icons here are kebab-case lucide names — same shape as the CMS
 * `icon` field. SpecIcon resolves them dynamically so we don't need
 * to pre-import the icon set.
 *
 * Keeping the legacy path means the page stays useful during the
 * migration window — once every product is re-saved with rich specs,
 * this static list can be deleted entirely.
 */
const LEGACY_CHARACTERISTICS: { key: keyof ProductSpecs; label: string; icon: string }[] = [
  { key: "material", label: "Material", icon: "cog" },
  { key: "diameter", label: "Diâmetro", icon: "circle-dot" },
  { key: "rivetableThickness", label: "Espessura Rebitável", icon: "ruler" },
  { key: "tensileStrength", label: "Resistência à Tração", icon: "gauge" },
  { key: "shearStrength", label: "Resistência ao Cisalhamento", icon: "zap" },
  { key: "tractionForce", label: "Tração", icon: "target" },
  { key: "weight", label: "Peso", icon: "weight" },
  { key: "finish", label: "Acabamento", icon: "paintbrush" },
  { key: "workingPressure", label: "Pressão de Trabalho", icon: "gauge" },
  { key: "minHoseDiameter", label: "Diâm. Interno Mín. Mangueira", icon: "cable" },
  { key: "airConsumption", label: "Consumo de Ar por Ciclo", icon: "wind" },
  { key: "maxPowerAt6Bar", label: "Potência Máxima em 6 BAR", icon: "flame" },
  { key: "outputVoltage", label: "Tensão de Saída para Solda", icon: "zap" },
  { key: "storedEnergy", label: "Energia Armazenada", icon: "flame" },
  { key: "maxWorkEnergy", label: "Energia Máxima de Trabalho", icon: "flame" },
  { key: "weldCapacity", label: "Capacidade de Solda", icon: "target" },
  { key: "dimensions", label: "Dimensões", icon: "ruler" },
  { key: "powerSource", label: "Fonte de Energia", icon: "zap" },
];

interface Row {
  /** Stable key for React reconciliation. */
  key: string;
  label: string;
  /** Pre-formatted display string (handles unit + range). */
  value: string;
  /** Kebab-case lucide icon name. Empty string = no icon. */
  icon: string;
}

const ProductOverview = ({ product, revisedAt }: ProductOverviewProps) => {
  // Accessories never get a spec table — keep the historical behaviour.
  const isAccessory = product.specs?.type === "Acessório";
  if (isAccessory) return null;

  // Phase 1 schema: prefer the rich `specEntries` array, which carries
  // CMS-controlled label / icon / formatted display. The legacy
  // `specs` object only kicks in when the CMS hasn't been re-saved yet.
  const rows: Row[] = product.specEntries && product.specEntries.length > 0
    ? product.specEntries.map((e) => ({
        key: e.filter_key ?? e.label,
        label: e.label,
        value: e.display,
        icon: e.icon ?? "",
      }))
    : LEGACY_CHARACTERISTICS
        .filter((c) => product.specs?.[c.key])
        .map((c) => ({
          key: String(c.key),
          label: c.label,
          value: String(product.specs[c.key]),
          icon: c.icon,
        }));

  if (rows.length === 0) return null;

  // If NO row has an icon, drop the icon column entirely — avoids the
  // visual weight of an empty column on text-only spec sets.
  const showIconColumn = rows.some((r) => !!r.icon);

  return (
    <section className="section-padding bg-background border-b border-border">
      <div className="container-industrial">
        {/* Centered to match the page's other section h2s (item 21).
            Was left-aligned with mb-8; now centered with mb-10 for
            consistent breathing room above the spec table. */}
        <h2 className="text-center text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-10">
          Características Técnicas
        </h2>
        <div className="overflow-hidden rounded-xl border border-border bg-card max-w-2xl mx-auto mb-12">
          <table className="w-full text-sm">
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.key}
                  className={`${i % 2 === 0 ? "bg-card" : "bg-muted/30"} border-b border-border/50 last:border-0`}
                >
                  {showIconColumn && (
                    <td className="px-5 py-4 w-12">
                      {row.icon ? (
                        <SpecIcon name={row.icon} className="w-4 h-4 text-primary" size={16} />
                      ) : null}
                    </td>
                  )}
                  <td className={`${showIconColumn ? "px-2" : "px-5"} py-4 font-medium text-muted-foreground whitespace-nowrap`}>
                    {row.label}
                  </td>
                  <td className="px-5 py-4 font-bold text-foreground text-right">
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Fallback home for the review date: only reached when this
            product has no variants table. Lives inside this section rather
            than beside it — `.product-bands` alternates the background of
            each direct child <section>, so an extra sibling would flip
            every band below it. */}
        {revisedAt && <SpecDateline iso={revisedAt} />}

        {/* CMS variant_table is intentionally NOT rendered here —
            its previous generic shape collided with the authored
            ModelSpecTable on /produtos/.../{slug} (CMS#11). The
            rebuild lives at the page level (ModelSpecTable) until a
            CMS-driven variant editor that matches that shape ships. */}
      </div>
    </section>
  );
};

export default ProductOverview;
